/**
 * MetricsMonetizationPage — 数据中台 · 商业化 L2-C（ADM-MTR-S004，1:1 移植原型 metrics-monetization.jsx）
 *
 * 付费渗透 × 首充 × ARPPU × 复购 四卡 + ARPPU/渗透率趋势 + 六级付费漏斗。数据走
 * useMetricsMonetization（GET /api/v1/metrics/monetization，admin 自有 API，随 P1-4 dev-docs 收录）。
 * 混合就绪度（禁假数据）：首充转化率 / 复购率 / 漏斗交易级次（新增·解锁·首充·复购）live；
 * ARPPU（及趋势）revenueReady 闸控；付费渗透率（分母 MAU 需 app_open 埋点）+ 漏斗行为级次
 * （开始阅读 / 进入充值页）恒 PENDING 灰显。空态（无交易/新增行）≠ PENDING。
 * 导出依赖收入列就绪，后端无端点 → 按原型保持禁用态（非假接线）。
 */

import type { MetricsRange } from '@novel-admin/shared';
import { useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import { useApps, useMetricsMonetization } from '~/data/queries';
import { Icons } from '~/lib/icons';
import { ChartSkeleton, KpiSkeleton } from '~/ui/Skeleton';
import { StatCard } from '~/ui/StatCard';
import { LineChartX } from '~/ui/charts';
import { IncomeCoverageNotice } from './IncomeCoverageNotice';
import {
  Cal,
  CardHead,
  LiveTag,
  MetricsToolbar,
  PENDING_REASON,
  PendTag,
  PendingBlock,
  PendingStat,
  StateGate,
  type StateGateMode,
  fmtUsdMinor,
} from './metrics-shared';

/** 漏斗行口径徽标（原型 MX_DATA.MONETIZATION.funnel 的 cal 列，按 step key 固定） */
const FUNNEL_STEP_CAL: Record<string, string> = {
  unlock_paid_chapter: 'Coin·非法币',
  first_charge: 'USD·运营口径',
  repurchase: 'USD·运营口径',
};

export default function MetricsMonetizationPage() {
  const appId = useAppScope((s) => s.appId);
  const [range, setRange] = useState<MetricsRange>('30d');

  const { data, isLoading, isError, hasData, refetch } = useMetricsMonetization({
    range,
    appScope: appId,
  });
  const { data: apps } = useApps();
  const currentAppName = appId === 'all' ? undefined : apps.find((a) => a.id === appId)?.name;

  // 四态派生（真实 query 状态，非原型演示 mode）：初次加载 → loading；失败 → error；
  // 成功但漏斗全部 live 级次均为 0（窗内无新增/解锁/付费行）→ empty；否则 normal
  const liveSteps = data.funnel.steps.filter((s) => s.live);
  const isEmpty =
    hasData &&
    !isError &&
    !data.revenueReady &&
    liveSteps.length > 0 &&
    liveSteps.every((s) => (s.value ?? 0) === 0);
  const mode: StateGateMode = isLoading
    ? 'loading'
    : isError
      ? 'error'
      : isEmpty
        ? 'empty'
        : 'normal';

  const freshness = data.window.to ? `数据更新至 ${data.window.to} · T+1` : undefined;

  // 漏斗条宽基准 = 首级（新增用户）真值；live 级次按占比出条，PENDING 级次虚线占位
  const funnelTop = Math.max(data.funnel.steps.find((s) => s.live)?.value ?? 0, 1);
  // 相邻两级均 live 才能给出级间转化率（中间隔 PENDING 级次 → —，不编造跨级率）
  const stepConv = (i: number): string | null => {
    if (i === 0) return null;
    const cur = data.funnel.steps[i];
    const prev = data.funnel.steps[i - 1];
    if (!cur?.live || !prev?.live || cur.value == null || prev.value == null || prev.value <= 0)
      return null;
    return ((cur.value / prev.value) * 100).toFixed(1);
  };

  return (
    <div className="page" data-screen-label="商业化 L2-C">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            商业化{' '}
            <span style={{ color: 'var(--fg-faint)', fontWeight: 400, fontSize: 15 }}>L2-C</span>
          </h1>
          <div className="page-sub hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            付费渗透 × 首充 × ARPPU × 复购 · 六级付费漏斗
            <Cal t="USD·运营口径·非结算" />
            <Cal t="T+1" />
            <LiveTag
              label="live·交易事实"
              title="真实数据源：users / purchase_amounts / unlock_records（C 端交易事实行）· 首充/复购/漏斗交易级次 live"
            />
            {!data.revenueReady && <PendTag k="rev" label="渗透+ARPPU PENDING" />}
          </div>
        </div>
        {/* 原型同款禁用导出：收入列未就绪且后端无导出端点，保持禁用态而非假接线 */}
        <button
          type="button"
          className="btn"
          disabled
          title="暂不支持导出"
          style={{ opacity: 0.55, cursor: 'not-allowed' }}
        >
          <Icons.IconDownload size={13} />
          导出
        </button>
      </div>

      <MetricsToolbar
        appName={currentAppName}
        range={range}
        setRange={setRange}
        freshness={freshness}
      />

      <IncomeCoverageNotice coverage={isError ? undefined : data.financialCoverage} />
      <StateGate
        mode={mode}
        skeleton={
          <>
            <KpiSkeleton count={4} />
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 12 }}>
              <ChartSkeleton height={240} />
              <ChartSkeleton height={240} />
            </div>
          </>
        }
        emptyTitle="当前范围暂无交易数据"
        emptyNote="该 App / 时间窗内 users / purchase_amounts / unlock_records 无记录"
        errorNote="GET /api/v1/metrics/monetization 加载失败 · 请重试"
        onRetry={refetch}
      >
        <>
          {/* 四卡：渗透恒 PENDING · 首充/复购 live · ARPPU 收入闸控（禁示例数字） */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <PendingStat
              label="付费渗透率"
              cal="MAU 口径"
              k="beh"
              note="分子=付费 distinct user（交易事实已就绪）· 分母 MAU 待埋点 app_open（口径待定）"
            />
            <StatCard
              label="首充转化率"
              value={data.kpi.firstChargeRate ?? '—'}
              delta={data.kpi.firstChargeDeltaPp ?? undefined}
              deltaSuffix="pp"
              deltaLabel="vs 上周期"
              decimals={1}
              spark={data.sparks.firstCharge}
              sparkColor="var(--chart-2)"
              format={(v) => (typeof v === 'number' ? `${v.toFixed(1)}%` : String(v))}
              hint="7 日窗口 · 新用户 7 日内首充 ÷ 成熟新增（cohort 7 日窗已完整）· live：users.first_payment_at"
            />
            {data.revenueReady ? (
              <StatCard
                label="ARPPU"
                value={data.kpi.arppuMinor ?? '—'}
                delta={data.kpi.arppuDeltaPct ?? undefined}
                deltaLabel="vs 上周期"
                spark={data.sparks.arppu}
                sparkColor="var(--chart-1)"
                format={(v) => (typeof v === 'number' ? fmtUsdMinor(v) : String(v))}
                hint="净收入 ÷ 付费用户 · USD·运营口径（PURCHASE−REFUND clamp≥0）"
              />
            ) : (
              <PendingStat
                label="ARPPU"
                cal="USD·运营口径"
                k="rev"
                note="净收入 ÷ 付费用户 · 待 purchase_amounts USD 列"
              />
            )}
            <StatCard
              label="复购率"
              value={data.kpi.repurchaseRate ?? '—'}
              delta={data.kpi.repurchaseDeltaPp ?? undefined}
              deltaSuffix="pp"
              deltaLabel="vs 上周期"
              decimals={1}
              spark={data.sparks.repurchase}
              sparkColor="var(--chart-4)"
              format={(v) => (typeof v === 'number' ? `${v.toFixed(1)}%` : String(v))}
              hint="≥2 单付费用户 ÷ 付费用户 · live：purchase_amounts"
            />
          </div>
          <div className="hstack" style={{ gap: 8, marginTop: -6, flexWrap: 'wrap' }}>
            <Cal t="渗透率分母=MAU" />
            <Cal t="ARPPU=USD·运营口径" />
            <Cal t="首充=7日窗口" />
            <Cal t="T+1" />
            <span style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
              口径已定稿并写入指标字典 ·{' '}
              {data.revenueReady ? '渗透率待埋点就绪后点亮' : '渗透率 / ARPPU 待上游就绪后点亮'}
            </span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1.5fr) minmax(0,1fr)',
              gap: 12,
            }}
          >
            {/* 趋势 · ARPPU 收入闸控（未就绪整图灰显）· 渗透率系列恒待埋点 */}
            <div className="card" style={{ padding: 14, minWidth: 0 }}>
              <CardHead
                title="ARPPU / 付费渗透率趋势"
                sub="30 天 · ARPPU 左轴（USD）· 渗透率同图（%）"
                tags={['USD·运营口径', 'T+1']}
                action={
                  data.revenueReady ? <PendTag k="beh" label="渗透·待埋点" /> : <PendTag k="rev" />
                }
              />
              {data.revenueReady ? (
                data.trend.arppuMinor.some((v) => v != null) ? (
                  <>
                    <LineChartX
                      xLabels={data.trend.xLabels}
                      series={[
                        {
                          label: 'ARPPU',
                          color: 'var(--chart-1)',
                          values: data.trend.arppuMinor.map((v) => (v == null ? null : v / 100)),
                        },
                      ]}
                      height={210}
                      yFormat={(v) => `$${v.toFixed(1)}`}
                    />
                    <div style={{ marginTop: 6, fontSize: 10.5, color: 'var(--fg-faint)' }}>
                      渗透率（%）系列待埋点 app_open 就绪后同图叠加 · 无付费日断点不补零
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      height: 232,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11.5,
                      color: 'var(--fg-faint)',
                    }}
                  >
                    近 30 天无付费记录 · 暂无 ARPPU 趋势
                  </div>
                )
              ) : (
                <PendingBlock
                  height={232}
                  schematic
                  icon="IconChart"
                  title="趋势待 purchase_amounts USD 列"
                  note="ARPPU 分子/分母均为权威口径（purchase_amounts × users）· 渗透率分母 MAU 另待埋点 app_open · 就绪后 nightly 回填自动出图"
                />
              )}
            </div>

            {/* 六级付费漏斗：交易级次 live · 行为级次恒 PENDING（分级次分别点亮） */}
            <div className="card" style={{ padding: 14, minWidth: 0 }}>
              <CardHead
                title="付费漏斗（六级）"
                sub="新增 → 阅读 → 解锁 → 充值页 → 首充 → 复购 · 权威级次与行为级次分别标注"
                tags={['混合口径']}
              />
              <div className="vstack" style={{ gap: 5 }}>
                {data.funnel.steps.map((f, i) => {
                  const conv = stepConv(i);
                  const stepCal = FUNNEL_STEP_CAL[f.key];
                  return (
                    <div
                      key={f.key}
                      title={f.pend ? PENDING_REASON[f.pend] : `live：${f.source}（server 权威）`}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: 4,
                        }}
                      >
                        <div className="hstack" style={{ gap: 7, flexWrap: 'wrap' }}>
                          <span
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 4,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'var(--bg-subtle)',
                              border: '1px solid var(--border)',
                              fontFamily: 'var(--font-mono)',
                              fontSize: 10,
                              color: 'var(--fg-muted)',
                            }}
                          >
                            {i + 1}
                          </span>
                          <span
                            style={{ fontSize: 12, color: f.pend ? 'var(--fg-muted)' : undefined }}
                          >
                            {f.label}
                          </span>
                          {stepCal && <Cal t={stepCal} />}
                          {f.pend && (
                            <PendTag
                              k={f.pend}
                              label={f.pend === 'rev' ? '权威级次·待收入' : '行为级次·待埋点'}
                            />
                          )}
                        </div>
                        <div className="hstack mono" style={{ fontSize: 11, gap: 10 }}>
                          {f.live && f.value != null ? (
                            <span style={{ fontWeight: 600, color: 'var(--fg-strong)' }}>
                              {f.value.toLocaleString()}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--fg-faint)' }}>—</span>
                          )}
                          <span style={{ color: 'var(--fg-faint)', width: 42, textAlign: 'right' }}>
                            {i > 0 ? (conv != null ? `${conv}%` : '—') : ''}
                          </span>
                        </div>
                      </div>
                      {f.live && f.value != null ? (
                        <div
                          style={{
                            height: 8,
                            borderRadius: 3,
                            background: 'var(--bg-muted)',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min((f.value / funnelTop) * 100, 100)}%`,
                              height: '100%',
                              background: 'linear-gradient(90deg, var(--chart-1), var(--chart-2))',
                              borderRadius: 3,
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            height: 8,
                            borderRadius: 3,
                            border: '1px dashed var(--border-strong)',
                            background: 'var(--bg-subtle)',
                          }}
                        />
                      )}
                      <div
                        className="mono"
                        style={{ fontSize: 9, color: 'var(--fg-faint)', marginTop: 2 }}
                      >
                        {f.source}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: '1px dashed var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                }}
              >
                <span style={{ color: 'var(--fg-muted)' }}>新增 → 首充整体转化</span>
                <span
                  className="mono"
                  style={{
                    color: data.funnel.overallRate != null ? 'var(--fg-strong)' : 'var(--fg-faint)',
                    fontWeight: data.funnel.overallRate != null ? 600 : undefined,
                  }}
                >
                  {data.funnel.overallRate != null ? `${data.funnel.overallRate.toFixed(1)}%` : '—'}
                </span>
              </div>
              <div
                style={{ marginTop: 8, fontSize: 10.5, color: 'var(--fg-faint)', lineHeight: 1.55 }}
              >
                权威级次（users / unlock_records / purchase_amounts）live · 行为级次（chapter_view /
                screen_view）待自建埋点 · 两类分别就绪、分别点亮
              </div>
            </div>
          </div>
        </>
      </StateGate>
    </div>
  );
}
